<template>
    <div class="content-wrapper">

        <!-- Search-first screen for Atención al Cliente users -->
        <div v-if="isAtencionCliente && !atencionClienteSearchSubmitted" class="row justify-content-center mt-5">
            <div class="col-md-8 col-lg-6">
                <div class="card card-with-shadow border-0 p-4">
                    <h5 class="card-title mb-3">
                        <app-icon name="search" class="mr-2" style="width:20px;height:20px;"/>
                        Buscar transacción
                    </h5>
                    <p class="text-muted mb-4">Ingresa el correo del cliente, el ICCID o una fecha para poder ver transacciones.</p>
                    <div class="input-group mb-3">
                        <input type="text"
                               class="form-control form-control-lg"
                               v-model="atencionClienteSearchQuery"
                               placeholder="Correo del cliente o ICCID"
                               @keyup.enter="submitAtencionClienteSearch"/>
                        <div class="input-group-append">
                            <button class="btn btn-primary" type="button" @click="submitAtencionClienteSearch">
                                Buscar
                            </button>
                        </div>
                    </div>
                    <div class="mb-3">
                        <input type="date"
                               class="form-control form-control-lg"
                               v-model="atencionClienteSearchDate">
                    </div>
                    <small class="text-muted">Debes buscar para poder ver transacciones.</small>
                </div>
            </div>
        </div>

        <!-- Main content: shown for non-atencion_cliente users OR after search submitted -->
        <template v-if="!isAtencionCliente || atencionClienteSearchSubmitted">
        <div class="row">
            <div class="col-sm-12 col-md-6">
                <app-breadcrumb :page-title="$t('transactions')" :directory="$t('transactions')" :icon="'list'"/>
            </div>
            <div class="col-sm-12 col-md-6 breadcrumb-side-button">
                <div v-if="!isAtencionCliente" class="float-md-right mb-3 mb-sm-3 mb-md-0">
                    <!-- Show unpaid transactions stats -->
                    <span v-if="showPaymentStats" class="badge badge-warning mr-2 p-2" style="font-size: 14px;">
                        {{ $t('unpaid_transactions') }}: {{ paymentStats.unpaid_count }} (${{ paymentStats.total_owed }})
                    </span>

                    <!-- Export Excel button -->
                    <a :href="exportUrl"
                       class="btn btn-info btn-with-shadow mr-2"
                       target="_blank">
                        <app-icon name="download" class="mr-1" style="width:16px;height:16px;"/>
                        {{ $t('export_excel') }}
                    </a>
                    
                    <!-- Mark as Paid button: only visible when a beneficiary is selected -->
                    <button v-if="isAdmin && beneficiarioFilter"
                            type="button"
                            class="btn btn-success btn-with-shadow mr-2"
                            @click="openMarkAsPaidModal">
                        {{ $t('mark_as_paid') }}
                    </button>
                    
                    <!-- Add button -->
                    <button type="button"
                            class="btn btn-primary btn-with-shadow"
                            data-toggle="modal"
                            @click="openAddEditModal">
                        {{ $t('add') }}
                    </button>
                </div>
            </div>
        </div>

        <!-- Filter buttons row -->
        <div v-if="!isAtencionCliente" class="row mb-3">
            <div class="col-12 d-flex align-items-center flex-wrap">
                <!-- Type filter (all / free / paid plans) -->
                <div class="btn-group mr-2 mb-1" role="group">
                    <button type="button" 
                            class="btn btn-sm"
                            :class="activeFilters.type === null ? 'btn-primary' : 'btn-outline-primary'"
                            @click="setFilter('type', null)">
                        {{ $t('all') }}
                    </button>
                    <button type="button" 
                            class="btn btn-sm"
                            :class="activeFilters.type === 'free' ? 'btn-primary' : 'btn-outline-primary'"
                            @click="setFilter('type', 'free')">
                        {{ $t('free') }}
                    </button>
                    <button type="button" 
                            class="btn btn-sm"
                            :class="activeFilters.type === 'paid' ? 'btn-primary' : 'btn-outline-primary'"
                            @click="setFilter('type', 'paid')">
                        {{ $t('payment_plans') }}
                    </button>
                </div>

                <!-- Super Partner filter: admin only (separate from Partner filter) -->
                <div v-if="isAdmin" class="mr-2 mb-1" style="min-width: 260px;">
                    <app-input type="search-select"
                               v-model="superPartnerFilter"
                               :list="superPartnersList"
                               :placeholder="$t('filter_by_super_partner') || 'Filtrar por Super Partner'"
                               @input="onSuperPartnerChange"/>
                </div>

                <!-- Partner filter: admin (all or filtered by SP) and super_partner (their own) -->
                <div v-if="canFilterByBeneficiario" class="mr-2 mb-1" style="min-width: 260px;">
                    <app-input type="search-select"
                               v-model="beneficiarioFilter"
                               :list="filteredBeneficiariosList"
                               :placeholder="$t('filter_by_partner') || 'Filtrar por Partner'"
                               @input="onBeneficiarioChange"/>
                </div>

                <!-- Beneficiary indicator for beneficiary users -->
                <div v-else-if="isPartnerScopedUser" class="d-inline-block mr-2 mb-1">
                    <span class="badge badge-info p-2" style="font-size: 13px;">
                        <app-icon name="filter" class="pr-1" style="width:14px;height:14px;"/>
                        {{ $t('my_transactions') }}
                    </span>
                </div>

                <!-- Payment status filter -->
                <div class="btn-group mr-2 mb-1" role="group">
                    <button type="button" 
                            class="btn btn-sm"
                            :class="activeFilters.payment_status === null ? 'btn-secondary' : 'btn-outline-secondary'"
                            @click="setFilter('payment_status', null)">
                        {{ $t('all_status') }}
                    </button>
                    <button type="button" 
                            class="btn btn-sm"
                            :class="activeFilters.payment_status === 'unpaid' ? 'btn-secondary' : 'btn-outline-secondary'"
                            @click="setFilter('payment_status', 'unpaid')">
                        {{ $t('unpaid') }}
                    </button>
                    <button type="button" 
                            class="btn btn-sm"
                            :class="activeFilters.payment_status === 'paid' ? 'btn-secondary' : 'btn-outline-secondary'"
                            @click="setFilter('payment_status', 'paid')">
                        {{ $t('paid') }}
                    </button>
                </div>
            </div>
        </div>

        <!-- Date range filter row -->
        <div v-if="!isAtencionCliente" class="row mb-3">
            <div class="col-12 d-flex align-items-center flex-wrap">
                <div class="mr-2 mb-1" style="min-width: 280px;">
                    <app-search @input="getSearchValue"/>
                </div>
                <div class="mr-2 mb-1" style="min-width: 170px;">
                    <app-input type="date"
                               v-model="activeFilters.start_date"
                               :placeholder="$t('start_date')"
                               @input="applyFilters"/>
                </div>
                <div class="mr-2 mb-1" style="min-width: 170px;">
                    <app-input type="date"
                               v-model="activeFilters.end_date"
                               :placeholder="$t('end_date')"
                               @input="applyFilters"/>
                </div>
                <div v-if="filterAmountLoading" class="mr-2 mb-1">
                    <span class="badge badge-secondary p-2" style="font-size: 13px;">
                        <app-icon name="loader" class="pr-1" style="width:14px;height:14px;"/>
                        {{ $t('loading') }}...
                    </span>
                </div>
                <div v-else-if="filterAmountResult !== null" class="mr-2 mb-1">
                    <span class="badge badge-info p-2" style="font-size: 14px;">
                        {{ $t('amount_owed') }}: ${{ filterAmountResult.amount }} ({{ filterAmountResult.count }} {{ $t('transactions') }})
                    </span>
                </div>
                <div v-if="saleCommissionTotal !== null" class="mr-2 mb-1">
                    <span class="badge badge-success p-2" style="font-size: 14px;">
                        Ganancia Venta: ${{ saleCommissionTotal }}
                    </span>
                </div>
                <button v-if="hasActiveFilters"
                        type="button"
                        class="btn btn-sm btn-outline-secondary mb-1"
                        @click="clearAllFilters">
                    {{ $t('clear') }}
                </button>
            </div>
        </div>

        <app-table :id="tableId" :options="tableOptions" :search="search" @action="getListAction"/>

        <add-modal v-if="isAddEditModalActive"
               :table-id="tableId"
               :selected-url="modalSelectedUrl"
                   @close-modal="closeAddEditModal"/>

        <detail-modal v-if="isDetailModalActive"
                      :transaction-id="rowData.id"
                      @close-modal="closeDetailModal"/>

        <mark-as-paid-modal v-if="isMarkAsPaidModalActive"
                           :table-id="tableId"
                           :prefill-beneficiario-id="beneficiarioFilter"
                           :prefill-start-date="activeFilters.start_date"
                           :prefill-end-date="activeFilters.end_date"
                           @close-modal="closeMarkAsPaidModal"/>

        <recharge-modal v-if="isRechargeModalActive"
                        :table-id="tableId"
                        :transaction="rowData"
                        @close-modal="closeRechargeModal"/>

        <app-delete-modal v-if="terminateConfirmationModalActive"
                          :preloader="terminateLoader"
                          modal-id="transaction-terminate"
                          @confirmed="confirmedTerminate"
                          @cancelled="cancelledTerminate"/>
        </template>
    </div>
</template>

<script>
    import CoreLibrary from "../../../../../core/helpers/CoreLibrary.js";
    import * as actions from "../../../../Config/ApiUrl";
    import {FormMixin} from '../../../../../core/mixins/form/FormMixin.js';
    import {TableWithoutWrapperMixin} from '../../../../Mixins/TableWithoutWrapperMixin.js';

    import AddModal from "./AddModal"; 
    import DetailModal from "./DetailModal";
    import MarkAsPaidModal from "./MarkAsPaidModal"; 
    import RechargeModal from "./RechargeModal";

    export default {
        extends: CoreLibrary,
        name: "TransactionsList",
        mixins: [FormMixin, TableWithoutWrapperMixin],
        props: {
            isAdminUser: {
                type: Boolean,
                default: null,
            }
        },
        components: {
            AddModal,
            DetailModal,
            MarkAsPaidModal,
            RechargeModal
        },
        data() {
            return {
                terminateLoader: false,
                isAddEditModalActive: false,
                isDetailModalActive: false,
                isMarkAsPaidModalActive: false,
                isRechargeModalActive: false,
                terminateConfirmationModalActive: false,
                modalSelectedUrl: '',
                tableId: 'transactions-table',
                rowData: {},
                // Search-first mode for atencion_cliente users
                atencionClienteSearchQuery: '',
                atencionClienteSearchDate: '',
                atencionClienteSearchSubmitted: false,
                // All active filters tracked in one place
                activeFilters: {
                    type: null,
                    beneficiario_id: '',
                    super_partner_id: '',
                    payment_status: null,
                    start_date: '',
                    end_date: '',
                },
                beneficiarioFilter: '',
                superPartnerFilter: '',
                filterAmountResult: null,
                filterAmountLoading: false,
                filterAmountTimer: null,
                saleCommissionTotal: null,
                beneficiariosList: [],
                superPartnersList: [],
                paymentStats: {
                    unpaid_count: 0,
                    total_owed: 0
                },
                options: {
                    url: actions.TRANSACTIONS,
                    name: this.$t('transactions'),
                    datatableWrapper: false,
                    showHeader: true,
                    columns: [
                        {
                            title: this.$t('transaction_id'),
                            type: 'text',
                            key: 'transaction_id',
                        },
                        {
                            title: this.$t('date'),
                            type: 'text',
                            key: 'creation_time',
                        },
                        {
                            title: this.$t('plan'),
                            type: 'text',
                            key: 'plan_name',
                            modifier: (value) => {
                                return value || 'N/A';
                            }
                        },
                        {
                            title: this.$t('data_amount'),
                            type: 'custom-html',
                            key: 'data_amount',
                            modifier: (value) => {
                                return value ? `${value} GB` : 'N/A';
                            }
                        },
                        {
                            title: this.$t('duration'),
                            type: 'custom-html',
                            key: 'duration_days',
                            modifier: (value) => {
                                return value ? `${value} ${this.$t('days')}` : 'N/A';
                            }
                        },
                        {
                            title: this.$t('purchase_amount'),
                            type: 'custom-html',
                            key: 'purchase_amount',
                            modifier: (value, row) => {
                                if (value === 0 || value === '0' || value === 0.0) {
                                    const reference = row.reference_purchase_amount
                                        ? ` <small class="text-muted">($${parseFloat(row.reference_purchase_amount).toFixed(2)})</small>`
                                        : '';
                                    return `<span class="badge badge-success">${this.$t('free')}</span>${reference}`;
                                }
                                return value ? `$${parseFloat(value).toFixed(2)}` : 'N/A';
                            }
                        },
                        {
                            title: this.$t('commission'),
                            type: 'custom-html',
                            key: 'commission_amount',
                            modifier: (value, row) => {
                                const commission = parseFloat(value || 0).toFixed(2);
                                return `$${commission}`;
                            }
                        },
                        {
                            title: 'Ganancia Venta',
                            type: 'custom-html',
                            key: 'partner_sale_commission_amount',
                            modifier: (value, row) => {
                                const partnerAmount = parseFloat(row.partner_sale_commission_amount || 0);
                                const spAmount = parseFloat(row.super_partner_sale_commission_amount || 0);

                                if (this.isBeneficiario) {
                                    if (!partnerAmount) return '<span class="text-muted">-</span>';
                                    return `<span class="badge badge-info">$${partnerAmount.toFixed(2)}</span>`;
                                }
                                if (this.isSuperPartner) {
                                    if (!spAmount) return '<span class="text-muted">-</span>';
                                    return `<span class="badge badge-info">$${spAmount.toFixed(2)}</span>`;
                                }
                                // Admin: show both
                                const parts = [];
                                if (partnerAmount) parts.push(`P: $${partnerAmount.toFixed(2)}`);
                                if (spAmount) parts.push(`SP: $${spAmount.toFixed(2)}`);
                                return parts.length ? parts.join(' / ') : '<span class="text-muted">-</span>';
                            }
                        },
                        {
                            title: 'Partner / Super Partner',
                            type: 'custom-html',
                            key: 'partner_name',
                            modifier: (value) => {
                                return value || 'N/A';
                            }
                        },
                        {
                            title: this.$t('client_name'),
                            type: 'custom-html',
                            key: 'cliente',
                            modifier: (value) => {
                                return value ? `${value.nombre} ${value.apellido}` : 'N/A';
                            }
                        },
                        {
                            title: this.$t('client_email'),
                            type: 'custom-html',
                            key: 'cliente',
                            modifier: (value) => {
                                return value && value.email ? value.email : 'N/A';
                            }
                        },
                        {
                            title: this.$t('status'),
                            type: 'text',
                            key: 'status',
                        },
                        {
                            title: this.$t('payment_status'),
                            type: 'custom-html',
                            key: 'is_paid',
                            modifier: (value) => {
                                if (value) {
                                    return `<span class="badge badge-success">${this.$t('paid')}</span>`;
                                }
                                return `<span class="badge badge-warning">${this.$t('unpaid')}</span>`;
                            }
                        },
                        {
                            title: this.$t('action'),
                            type: 'action',
                            key: 'invoice',
                            isVisible: true
                        }
                    ],
                    actions: [
                        {
                            title: this.$t('details'),
                            icon: 'eye',
                            type: 'none',
                            component: 'app-detail-modal',
                            modalId: 'transaction-detail-modal',
                        }, {
                            title: this.$t('edit'),
                            icon: 'edit',
                            type: 'none',
                            component: 'app-add-modal',
                            modalId: 'transaction-add-edit-modal',
                        }, {
                            title: this.$t('recharge_esim'),
                            icon: 'refresh-cw',
                            type: 'none',
                            component: 'app-recharge-modal',
                            modalId: 'transaction-recharge-modal',
                        }, {
                            title: this.$t('send_recharge_email'),
                            icon: 'mail',
                            type: 'none',
                            component: 'app-send-recharge-email-action',
                            modalId: 'transaction-send-recharge-email',
                        },
                    ],
                    showFilter: false,
                    showSearch: true,
                    paginationType: "pagination",
                    responsive: true,
                    rowLimit: 10,
                    showAction: true,
                    orderBy: 'desc',
                    actionType: "default",
                }
            }
        },
        computed: {
            isAdmin() {
                if (typeof this.isAdminUser === 'boolean') {
                    return this.isAdminUser;
                }

                return this.$store.state.user && 
                       this.$store.state.user.loggedInUser && 
                       (this.$store.state.user.loggedInUser.role === 'Admin' || 
                        this.$store.state.user.loggedInUser.user_type === 'admin');
            },
            isSuperPartner() {
                return this.$store.state.user &&
                       this.$store.state.user.loggedInUser &&
                       this.$store.state.user.loggedInUser.user_type === 'super_partner';
            },
            isSuperPartnerScopedUser() {
                const user = this.$store.state.user && this.$store.state.user.loggedInUser;

                return !!user && ['super_partner', 'admin_partner'].includes(user.user_type);
            },
            isAtencionCliente() {
                const u = this.$store.state.user && this.$store.state.user.loggedInUser;
                return u && u.user_sub_type === 'atencion_cliente';
            },
            canTerminateSubscriptions() {
                const u = this.$store.state.user && this.$store.state.user.loggedInUser;

                if (!u) {
                    return false;
                }

                return u.user_type === 'admin';
            },
            tableOptions() {
                if (this.isAtencionCliente) {
                    const allowedActions = this.options.actions.filter(action => {
                        return [this.$t('details'), this.$t('send_recharge_email')].includes(action.title);
                    });

                    return {
                        ...this.options,
                        actions: allowedActions,
                        showAction: allowedActions.length > 0,
                    };
                }

                if (!this.canTerminateSubscriptions) {
                    return this.options;
                }
                return {
                    ...this.options,
                    actions: [
                        ...this.options.actions,
                        {
                            title: this.$t('terminate_subscription'),
                            icon: 'slash',
                            type: 'none',
                            component: 'app-confirmation-modal',
                            modalId: 'transaction-terminate',
                        }
                    ]
                };
            },
            isBeneficiario() {
                return this.$store.state.user && 
                       this.$store.state.user.loggedInUser && 
                       this.$store.state.user.loggedInUser.user_type === 'beneficiario';
            },
            isPartnerScopedUser() {
                const user = this.$store.state.user && this.$store.state.user.loggedInUser;

                return !!user && ['beneficiario', 'admin_beneficiario'].includes(user.user_type);
            },
            canFilterByBeneficiario() {
                // Admin ve todos los beneficiarios; super_partner solo los suyos.
                return this.isAdmin || this.isSuperPartnerScopedUser;
            },
            // Partners list filtered by selected super partner (admin only)
            filteredBeneficiariosList() {
                if (!this.isAdmin || !this.superPartnerFilter) {
                    return this.beneficiariosList;
                }
                // Keep only the "all" option plus partners that belong to the selected super partner
                const spId = parseInt(this.superPartnerFilter, 10);
                return this.beneficiariosList.filter(option => {
                    if (!option.id || option.id === 'none') return true;
                    if (option.id.toString().startsWith('beneficiario:')) {
                        return option.super_partner_id === spId;
                    }
                    return true;
                });
            },
            showPaymentStats() {
                return this.paymentStats.unpaid_count > 0;
            },
            hasActiveFilters() {
                  return this.search ||
                      this.beneficiarioFilter ||
                      this.superPartnerFilter ||
                       this.activeFilters.type !== null ||
                       this.activeFilters.payment_status !== null ||
                       this.activeFilters.start_date ||
                       this.activeFilters.end_date;
            },
            // Build export URL with all current filters
            exportUrl() {
                const params = new URLSearchParams();
                // Decode current selection into beneficiario_id or super_partner_id
                if (this.activeFilters.beneficiario_id) {
                    params.append('beneficiario_id', this.activeFilters.beneficiario_id);
                }
                if (this.activeFilters.super_partner_id) {
                    params.append('super_partner_id', this.activeFilters.super_partner_id);
                }
                if (this.activeFilters.type) params.append('type', this.activeFilters.type);
                if (this.activeFilters.payment_status !== null && this.activeFilters.payment_status !== '') {
                    params.append('payment_status', this.activeFilters.payment_status);
                }
                if (this.activeFilters.start_date) params.append('start_date', this.formatDateParam(this.activeFilters.start_date));
                if (this.activeFilters.end_date) params.append('end_date', this.formatDateParam(this.activeFilters.end_date));
                const qs = params.toString();
                return `/transactions/export${qs ? '?' + qs : ''}`;
            },
            // Only pass a plain beneficiario ID to the mark-as-paid modal
            prefillBeneficiarioId() {
                const value = this.beneficiarioFilter;

                if (!value) {
                    return '';
                }

                if (value === 'none') {
                    return 'none';
                }

                if (value.startsWith('beneficiario:')) {
                    return value.split(':')[1];
                }

                // For super partners or unknown formats, don't prefill
                return '';
            }
        },
        mounted() {
            if (this.isAtencionCliente) {
                return;
            }

            this.loadPaymentStats();
            this.loadBeneficiarios();
            this.loadSaleCommissionTotal();
        },
        watch: {
            canFilterByBeneficiario(newValue, oldValue) {
                if (newValue && !oldValue) {
                    this.loadBeneficiarios();
                }
            },
        },
        methods: {
            loadPaymentStats() {
                const params = new URLSearchParams();
                if (this.activeFilters.beneficiario_id) params.append('beneficiario_id', this.activeFilters.beneficiario_id);
                if (this.activeFilters.super_partner_id) params.append('super_partner_id', this.activeFilters.super_partner_id);
                if (this.activeFilters.type) params.append('type', this.activeFilters.type);
                if (this.activeFilters.start_date) params.append('start_date', this.activeFilters.start_date);
                if (this.activeFilters.end_date) params.append('end_date', this.activeFilters.end_date);

                this.axiosGet(actions.TRANSACTIONS_PAYMENT_STATS + (params.toString() ? '?' + params.toString() : ''))
                    .then(response => {
                        this.paymentStats = response.data;
                    })
                    .catch(error => {
                        console.error('Error loading payment stats:', error);
                    });
            },

            loadBeneficiarios() {
                if (!this.canFilterByBeneficiario) {
                    this.beneficiariosList = [];
                    this.superPartnersList = [];
                    return;
                }

                this.axiosGet(actions.BENEFICIARIOS + '?per_page=1000')
                    .then(response => {
                        const payload = response && response.data ? response.data : {};
                        const beneficiarios = Array.isArray(payload.data)
                            ? payload.data
                            : (payload.data && Array.isArray(payload.data.data) ? payload.data.data : []);

                        const beneficiarioOptions = beneficiarios.map(beneficiario => ({
                            id: `beneficiario:${beneficiario.id}`,
                            value: beneficiario.nombre,
                            super_partner_id: beneficiario.super_partner_id,
                        }));

                        this.beneficiariosList = [
                            { id: '', value: this.$t('all_partners') || 'Todos los Partners' },
                            ...beneficiarioOptions,
                            { id: 'none', value: this.$t('without_partner') || 'Sin Partner' },
                        ];

                        if (this.isSuperPartnerScopedUser) {
                            this.superPartnersList = [];
                            return null;
                        }

                        return this.axiosGet(actions.SUPER_PARTNERS + '?per_page=1000');
                    })
                    .then(response => {
                        if (response && response.data) {
                            const payload = response.data;
                            const superPartners = Array.isArray(payload.data)
                                ? payload.data
                                : (payload.data && Array.isArray(payload.data.data) ? payload.data.data : []);

                            this.superPartnersList = [
                                { id: '', value: this.$t('all_super_partners') || 'Todos los Super Partners' },
                                ...superPartners.map(sp => ({
                                    id: sp.id.toString(),
                                    value: sp.nombre,
                                })),
                            ];
                        }
                    })
                    .catch(error => {
                        console.error('Error loading beneficiaries or super partners:', error);
                    });
            },

            /**
             * Set a single filter value and rebuild the table URL.
             */
            setFilter(key, value) {
                if (key === 'beneficiario_id') {
                    this.beneficiarioFilter = value;
                }
                this.activeFilters[key] = value;
                this.applyFilters();
            },

            onBeneficiarioChange() {
                // Only reset beneficiario_id; keep super_partner_id from the SP dropdown
                this.activeFilters.beneficiario_id = '';

                const value = this.beneficiarioFilter;

                if (value === 'none') {
                    this.activeFilters.beneficiario_id = 'none';
                } else if (value && value.startsWith('beneficiario:')) {
                    this.activeFilters.beneficiario_id = value.split(':')[1];
                }

                this.applyFilters();
            },

            onSuperPartnerChange() {
                // Update super_partner_id filter and reset partner filter
                this.activeFilters.super_partner_id = this.superPartnerFilter || '';
                this.beneficiarioFilter = '';
                this.activeFilters.beneficiario_id = '';
                this.applyFilters();
            },

            /**
             * Rebuild options.url with all active filter params so the datatable
             * always sends all filters on each reload.
             */
            applyFilters() {
                const params = new URLSearchParams();
                if (this.activeFilters.beneficiario_id) params.append('beneficiario_id', this.activeFilters.beneficiario_id);
                if (this.activeFilters.super_partner_id) params.append('super_partner_id', this.activeFilters.super_partner_id);
                if (this.activeFilters.type) params.append('type', this.activeFilters.type);
                if (this.activeFilters.payment_status !== null && this.activeFilters.payment_status !== '') {
                    params.append('payment_status', this.activeFilters.payment_status);
                }
                if (this.activeFilters.start_date) params.append('start_date', this.activeFilters.start_date);
                if (this.activeFilters.end_date) params.append('end_date', this.activeFilters.end_date);

                const qs = params.toString();
                this.options.url = actions.TRANSACTIONS + (qs ? '?' + qs : '');

                this.$nextTick(() => {
                    this.$hub.$emit('reload-' + this.tableId);
                });

                if (this.isAtencionCliente) {
                    return;
                }

                this.loadPaymentStats();
                this.loadSaleCommissionTotal();

                const hasBeneficiarioFilter = !!this.activeFilters.beneficiario_id;
                const hasSuperPartnerFilter = !!this.activeFilters.super_partner_id;
                const hasDateFilters = !!(this.activeFilters.start_date || this.activeFilters.end_date);

                if (hasBeneficiarioFilter || hasSuperPartnerFilter || hasDateFilters) {
                    this.fetchFilterAmount();
                } else {
                    this.filterAmountResult = null;
                }
            },

            fetchFilterAmount() {
                if (this.filterAmountTimer) {
                    clearTimeout(this.filterAmountTimer);
                }
                const hasFilters = this.activeFilters.beneficiario_id || this.activeFilters.super_partner_id || this.activeFilters.start_date || this.activeFilters.end_date;
                if (!hasFilters) {
                    this.filterAmountResult = null;
                    return;
                }
                this.filterAmountTimer = setTimeout(() => {
                    this.filterAmountLoading = true;
                    const params = new URLSearchParams();
                    if (this.activeFilters.beneficiario_id) params.append('beneficiario_id', this.activeFilters.beneficiario_id);
                    if (this.activeFilters.super_partner_id) params.append('super_partner_id', this.activeFilters.super_partner_id);
                    if (this.activeFilters.type) params.append('type', this.activeFilters.type);
                    if (this.activeFilters.start_date) params.append('start_date', this.activeFilters.start_date);
                    if (this.activeFilters.end_date) params.append('end_date', this.activeFilters.end_date);
                    this.axiosGet(actions.TRANSACTIONS_CALCULATE_AMOUNT + '?' + params.toString())
                        .then(response => {
                            this.filterAmountResult = response.data;
                        })
                        .catch(error => {
                            console.error('Error calculating payment amount:', error);
                        })
                        .finally(() => {
                            this.filterAmountLoading = false;
                        });
                }, 500);
            },

            loadSaleCommissionTotal() {
                const params = new URLSearchParams();
                if (this.activeFilters.beneficiario_id) params.append('beneficiario_id', this.activeFilters.beneficiario_id);
                if (this.activeFilters.super_partner_id) params.append('super_partner_id', this.activeFilters.super_partner_id);
                if (this.activeFilters.type) params.append('type', this.activeFilters.type);
                if (this.activeFilters.payment_status !== null && this.activeFilters.payment_status !== '') {
                    params.append('payment_status', this.activeFilters.payment_status);
                }
                if (this.activeFilters.start_date) params.append('start_date', this.formatDateParam(this.activeFilters.start_date));
                if (this.activeFilters.end_date) params.append('end_date', this.formatDateParam(this.activeFilters.end_date));

                this.axiosGet(actions.TRANSACTIONS_SALE_COMMISSION_TOTAL + (params.toString() ? '?' + params.toString() : ''))
                    .then(response => {
                        const data = response.data;
                        if (data && typeof data.total !== 'undefined') {
                            const total = parseFloat(data.total);
                            this.saleCommissionTotal = total > 0 ? total.toFixed(2) : null;
                        } else {
                            this.saleCommissionTotal = null;
                        }
                    })
                    .catch(() => {
                        this.saleCommissionTotal = null;
                    });
            },

            clearAllFilters() {
                this.search = '';
                this.beneficiarioFilter = '';
                this.superPartnerFilter = '';
                this.activeFilters = {
                    type: null,
                    beneficiario_id: '',
                    super_partner_id: '',
                    payment_status: null,
                    start_date: '',
                    end_date: '',
                };
                this.filterAmountResult = null;
                this.saleCommissionTotal = null;
                this.options.url = actions.TRANSACTIONS;
                this.$nextTick(() => {
                    this.$hub.$emit('reload-' + this.tableId);
                });

                if (this.isAtencionCliente) {
                    return;
                }

                this.loadPaymentStats();
                this.loadSaleCommissionTotal();
            },

            openAddEditModal() {
                this.isAddEditModalActive = true;
            },

            closeAddEditModal() {
                $("#transaction-add-edit-modal").modal('hide'); 
                this.isAddEditModalActive = false;
                this.reSetData();
            },

            openMarkAsPaidModal() {
                this.isMarkAsPaidModalActive = true;
            },

            closeMarkAsPaidModal() {
                $("#mark-as-paid-modal").modal('hide');
                this.isMarkAsPaidModalActive = false;
                this.loadPaymentStats();
            },

            getListAction(rowData, actionObj, active) {
                this.rowData = rowData;

                if (actionObj.title == this.$t('edit')) {
                    this.modalSelectedUrl = `${actions.TRANSACTIONS}/${rowData.id}`;
                    this.openAddEditModal();
                } else if (actionObj.title == this.$t('details')) {
                    this.openDetailModal();
                } else if (actionObj.title == this.$t('recharge_esim')) {
                    this.openRechargeModal();
                } else if (actionObj.title == this.$t('send_recharge_email')) {
                    this.sendRechargeEmail(rowData);
                } else if (actionObj.title == this.$t('terminate_subscription')) {
                    this.openTerminateModal();
                }
            },

            sendRechargeEmail(rowData) {
                this.axiosPost({
                    url: actions.TRANSACTIONS_SEND_RECHARGE_EMAIL(rowData.id),
                }).then(response => {
                    this.$toastr.s(response.data.message || this.$t('recharge_email_sent'));
                }).catch(error => {
                    const message = error.response?.data?.message || this.$t('recharge_email_failed');
                    this.$toastr.e(message);
                });
            },

            openRechargeModal() {
                this.isRechargeModalActive = true;
            },

            closeRechargeModal() {
                $("#transaction-recharge-modal").modal('hide');
                this.isRechargeModalActive = false;
                this.reSetData();
            },

            openDetailModal() {
                this.isDetailModalActive = true;
            },

            closeDetailModal() {
                $("#transaction-detail-modal").modal('hide');
                this.isDetailModalActive = false;
                this.reSetData();
            },

            openTerminateModal() {
                this.terminateConfirmationModalActive = true;
            },

            confirmedTerminate() {
                this.terminateLoader = true;
                this.axiosPost({
                    url: `/transactions/${this.rowData.id}/terminate-subscription`,
                    data: {}
                }).then(response => {
                    this.terminateLoader = false;
                    $("#transaction-terminate").modal('hide');
                    this.cancelledTerminate();
                    this.$toastr.s(response.data.message);
                }).catch(error => {
                    this.terminateLoader = false;
                    this.$toastr.e(error.response?.data?.message || this.$t('error_terminating_subscription'));
                }).finally(() => {
                    this.$hub.$emit('reload-' + this.tableId);
                });
            },

            cancelledTerminate() {
                this.terminateConfirmationModalActive = false;
                this.reSetData();
            },

            reSetData() {
                this.rowData = {};
                this.modalSelectedUrl = '';
            },

            submitAtencionClienteSearch() {
                const query = this.atencionClienteSearchQuery.trim();

                if (!query && !this.atencionClienteSearchDate) {
                    return;
                }

                this.search = query;
                this.activeFilters.start_date = this.atencionClienteSearchDate || '';
                this.activeFilters.end_date = this.atencionClienteSearchDate || '';
                this.atencionClienteSearchSubmitted = true;
                this.applyFilters();
            },

            /**
             * Format a date value (Date object or string) to YYYY-MM-DD for URL params.
             */
            formatDateParam(date) {
                const d = new Date(date);
                return isNaN(d.getTime()) ? String(date) : d.toISOString().split('T')[0];
            }
        }
    }
</script>
